import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, TemplateRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { SavedView, SavedViewsService } from './saved-views.service';

@Component({
  selector: 'app-saved-views',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './saved-views.component.html',
})
export class SavedViewsComponent implements OnInit {
  @Input() filters: Record<string, unknown> = {};
  @Input() context: Record<string, unknown> = {};
  @Output() viewSelected = new EventEmitter<SavedView | null>();
  private readonly destroyRef = inject(DestroyRef);
  views: SavedView[] = [];
  selectedId: string | null = null;
  editing: SavedView | null = null;
  name = '';
  isDefault = false;
  loading = true;
  saving = false;
  error = false;
  private modalRef?: NgbModalRef;

  constructor(
    public service: SavedViewsService,
    private modal: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.destroyRef.onDestroy(() => this.modalRef?.dismiss());
  }

  ngOnInit(): void {
    let initialized = false;
    combineLatest([
      this.service.list(),
      this.route.queryParams.pipe(distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current))),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([views, params]) => {
        const firstLoad = !initialized;
        if (firstLoad) this.views = views.filter(view => view.scope === this.service.scope);
        initialized = true;
        if (this.service.scope === '/my-folders' && params['search']?.trim() && !params['savedView']) {
          this.select(null, false);
          this.loading = false;
          return;
        }
        const view = this.views.find(item => item.isDefault)
          ?? this.views.find(item => item.id === params['savedView']);
        if (firstLoad && view) this.select(view);
        this.loading = false;
      },
      error: () => { this.loading = false; this.error = true; },
    });
  }

  select(view: SavedView | null, syncUrl = true): void {
    this.selectedId = view?.id ?? null;
    if (syncUrl) {
      from(this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { savedView: this.selectedId }, queryParamsHandling: 'merge', replaceUrl: true,
      })).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.viewSelected.emit(view));
    } else {
      this.viewSelected.emit(view);
    }
  }

  configure(template: TemplateRef<unknown>, view: SavedView | null): void {
    this.editing = view;
    this.name = view?.name ?? '';
    this.isDefault = view?.isDefault ?? false;
    this.error = false;
    this.modalRef = this.modal.open(template, { centered: true, backdrop: 'static' });
  }

  save(asCopy = false): void {
    if (this.saving || !this.name.trim()) return;
    this.saving = true;
    this.error = false;
    // Editing an inactive view must keep its own filters.
    const filters = this.editing && this.editing.id !== this.selectedId
      ? this.editing.filters : this.filters;
    const id = asCopy ? undefined : this.editing?.id;
    const previousIds = new Set(this.views.map(view => view.id));
    this.service.save({
      name: this.name.trim(), scope: this.service.scope, filters, isDefault: this.isDefault,
      context: this.editing && this.editing.id !== this.selectedId ? this.editing.context : this.context,
    }, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: views => {
        this.views = views.filter(view => view.scope === this.service.scope);
        const saved = this.views.find(view => id ? view.id === id : !previousIds.has(view.id));
        if (saved) this.select(saved);
        this.saving = false;
        this.modalRef?.close();
      },
      error: () => { this.saving = false; this.error = true; },
    });
  }

  remove(): void {
    if (!this.editing || this.saving) return;
    const id = this.editing.id;
    this.saving = true;
    this.error = false;
    this.service.delete(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: views => {
        this.views = views.filter(view => view.scope === this.service.scope);
        if (this.selectedId === id) this.select(null);
        this.saving = false;
        this.modalRef?.close();
      },
      error: () => { this.saving = false; this.error = true; },
    });
  }
}
